import { debounce, get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as EmptyViewExam } from '../../../assets/images/icons/mevo/exam/empty-view.svg';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Divider } from '../../../components/UI-CHAKRA/Divider';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Textarea } from '../../../components/UI-CHAKRA/Input/Textarea';
import SearchIcd from '../../../components/UI-CHAKRA/SearchIcd';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Anchor } from '../../../components/notifications/Anchor';
import { ANCHOR_POS, ANCHORS } from '../../../components/notifications/notifications';
import ExamCard from '../../../components/search/ExamCard';
import { scrollTo } from '../../../helpers/window';
import { Exam, FavoriteExam } from '../../../interfaces/exam';
import { OriginEnum } from '../../../interfaces/medicine';
import { useExamStore } from '../../../stores/examStore';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { Icd } from '../../my-central/patientService';
import PrescriptionExamSummary from '../PrescriptionExamSummary/PrescriptionExamSummary';
import { EXAM_INSTRUCTIONS_LIMIT } from './PrescriptionExamContent';

const EmptyView = () => {
  return (
    <Flex flexDir={'column'} alignItems={'center'} justifyContent={'center'} gap={'10px'} p={'10px 30px 30px'}>
      <EmptyViewExam />
      <Flex flexDir={'column'} alignItems={'center'} justifyContent={'center'}>
        <Text fontSize={'15px'} color={'primary.main'} lineHeight={'18px'} fontWeight={600}>
          Ainda não há exames adicionados
        </Text>
        <Text fontSize={'12px'} fontWeight={400} lineHeight={'14px'} color={'typography.dark'}>
          Use a busca acima para adicionar o primeiro exame
        </Text>
      </Flex>
    </Flex>
  );
};

interface IncludedExamsProps {
  exams: Exam[];
  favoriteExams: FavoriteExam[];
  onUpdateGeneralDataExam(structuredIcd: Icd | null, observation: string): void;
  onDeleteExam(exam: Exam): void;
  onUpdateExam(payload: Exam): Promise<void>;
  enableTussCodeOnInterface: boolean;
  onFavoriteExam?(exam: Exam): Promise<void>;
  registerExamConfirmCallback?: (index: number, cb: () => Promise<void>) => void;
  needsLaterality?: boolean;
  handleSetLaterality: (value: boolean, lateralityFilled: boolean) => void;
  structuredCid: Icd | null;
}

export default function IncludedExams(props: IncludedExamsProps) {
  const { exams, onUpdateExam, onUpdateGeneralDataExam, onDeleteExam, enableTussCodeOnInterface, onFavoriteExam, structuredCid } =
    props;

  const { prescriberConfiguration, prescription } = usePrescriptionStore();

  const mustSetObs = get(prescriberConfiguration, 'Features.enableMandatoryClinicalIndication', false);

  const [, setIsFocused] = useState(false);

  const {
    setDefaultExamIndex,
    defaultExamIndex,
    generalExamIcd,
    generalExamObservation,
    setGeneralExamObservation,
    setGeneralExamIcd,
    examIcdHasError,
    examObservationError,
    setExamIcdHasError,
    setExamObservationError,
  } = useExamStore();

  const [icd, setIcd] = useState<Icd | null>(
    generalExamIcd
      ? {
        description: generalExamIcd.Descricao,
        id: generalExamIcd.idCID10,
        subcategory: generalExamIcd.Codigo,
      } :
      structuredCid ? structuredCid
        : null
  );
  const [lastGeneralIcd, setLastGeneralIcd] = useState<Icd | null>(icd)
  const [observation, setObservation] = useState<string>(generalExamObservation);

  const onUpdateGeneralDataExamRef = React.useRef(onUpdateGeneralDataExam);
  useEffect(() => {
    onUpdateGeneralDataExamRef.current = onUpdateGeneralDataExam;
  }, [onUpdateGeneralDataExam]);

  const debouncedOnUpdateGeneralDataExam = useMemo(
    () => debounce((...args: Parameters<typeof onUpdateGeneralDataExam>) => {
      onUpdateGeneralDataExamRef.current(...args);
    }, 600),
    []
  );

  const onChangeGeneralIcd = useCallback(async (selectedIcd: Icd | null) => {
    setExamIcdHasError(false);
    debouncedOnUpdateGeneralDataExam(selectedIcd, observation);
    setGeneralExamIcd(
      selectedIcd
        ? {
          Codigo: selectedIcd.subcategory,
          idCID10: selectedIcd.id,
          Descricao: selectedIcd.description,
        }
        : null
    );

    setLastGeneralIcd(icd);
    setIcd(selectedIcd);

    const updatePromises = exams
      .filter((exam) => !exam.CidEspecifico)
      .map((exam) => {
        if (selectedIcd) {
          const icdStructured = {
            idCID10: selectedIcd.id,
            Codigo: selectedIcd.subcategory,
            Descricao: selectedIcd.description,
          };
          return onUpdateExam({
            ...exam,
            CID10Estruturado: [icdStructured],
          });
        } else {
          return onUpdateExam({
            ...exam,
            CID10Estruturado: [],
          });
        }
      });

    await Promise.all(updatePromises);
  }, [debouncedOnUpdateGeneralDataExam, observation, setExamIcdHasError, setGeneralExamIcd, exams, onUpdateExam, icd]);

  const onChangeObservation = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    setExamObservationError(false);
    setObservation(newValue);
    setGeneralExamObservation(newValue);
  };

  const onBlurObservation = async () => {
    setIsFocused(false);
  };

  useEffect(() => {
    if (defaultExamIndex < 5) return;

    const anchor = document.getElementById(`included-exam-${defaultExamIndex}`);

    if (anchor) {
      const scrollTimeoutId = setTimeout(() => {
        scrollTo('Container', {
          top: anchor.getBoundingClientRect().top + window.scrollY,
        });
      }, 500);

      return () => {
        clearTimeout(scrollTimeoutId);
        setDefaultExamIndex(0);
      };
    }
  }, [defaultExamIndex, setDefaultExamIndex]);

  useEffect(() => {
    if (!icd && structuredCid && structuredCid !== icd) {
      onChangeGeneralIcd(structuredCid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuredCid])

  return (
    <Box boxShadow={'0px 4px 104px 0px rgba(0, 0, 0, 0.05)'} w={'100%'}>
      <Anchor anchorName={ANCHORS.EXAM_DETAILS} position={ANCHOR_POS.BOTTOM}>
        <Box
          bg={'#fff'}
          padding={'18px 20px 15px 20px'}
          display={'flex'}
          flexDir={'column'}
          gap={'8px'}
          borderRadius={'10px 10px 0 0'}
        >
          <Flex alignItems={'center'} justifyContent={'space-between'}>
            <Text fontSize={'14px'} fontWeight={600} color={'primary.main'}>
              Exames Selecionados
            </Text>
            {exams.length > 0 && (
              <Box borderRadius={'14px'} bg={'#F2EEFB'} p={'4px 8px'}>
                <Text fontSize={'12px'} fontWeight={700} color={'primary.main'}>
                  {exams.length} exame{exams.length > 1 ? 's' : ''}
                </Text>
              </Box>
            )}
          </Flex>
          <Flex flexDir={'column'} gap={'10px'}>
            <Text fontSize={'12px'} fontWeight={700} color={'primary.main'}>
              indicação clínica
            </Text>
            <SearchIcd
              label={''}
              icd={icd}
              setIcd={onChangeGeneralIcd}
              menuMaxHeight={'170px'}
              placeholder={'Buscar CID'}
              hasError={examIcdHasError}
            />
          </Flex>

          <Flex flexDir={'column'} gap={'10px'}>
            <Text fontSize={'12px'} fontWeight={700} color={'primary.main'}>
              observação/justificativa
            </Text>
            <Textarea
              resize={'none'}
              value={observation}
              onChange={onChangeObservation}
              placeholder={'Aplica-se a todas às guias'}
              maxLength={EXAM_INSTRUCTIONS_LIMIT}
              borderColor={examObservationError ? '#E53E3E' : '#CBBFE6'}
              rows={3}
              onFocus={() => setIsFocused(true)}
              onBlur={onBlurObservation}
              transition={'0.3s all'}
              height={'40px'}
              _focus={{
                height: '80px',
                border: 'none',
              }}
            />
          </Flex>
        </Box>
      </Anchor>

      <Box
        borderRadius={'10px'}
        boxShadow={'0px 4px 104px 0px rgba(0, 0, 0, 0.05)'}
        bg={'#F9F9FA'}
        padding={'12px 20px'}
      >
        {(exams || []).length > 0 ? (
          <Flex flexDir={'column'} gap={'10px'}>
            {(exams || []).map((exam, index) => {
              return (
                <ExamCard
                  key={exam.idPrescricaoExame || index}
                  exam={exam}
                  index={index + 1}
                  onDelete={onDeleteExam}
                  onUpdateExam={onUpdateExam}
                  onFavoriteExam={onFavoriteExam}
                  onEdit={() => { }}
                  enableTuss={enableTussCodeOnInterface}
                  hideIndex={true}
                  cardIndex={index}
                  shouldCollapse={true}
                  isFavorite={props.favoriteExams.some((favoriteExam) => favoriteExam.referencia === exam.referencia)}
                  registerConfirmCallback={(cb: any) => props.registerExamConfirmCallback?.(index, cb)}
                  shouldBeOpen={true}
                  needsLaterality={props.needsLaterality}
                  handleSetLaterality={props.handleSetLaterality}
                  disabled={exam.Origem === OriginEnum.MEDFLOW}
                  structuredIcd={icd}
                  lastGeneralIcd={lastGeneralIcd}
                />
              );
            })}
          </Flex>
        ) : (
          <EmptyView />
        )}
        <Divider m={'15px 0'} bg={''} />
        <PrescriptionExamSummary />
      </Box>
    </Box>
  );
}
